/**
 * Первый вход (ТЗ UI-36, UI-37).
 *
 * До этого экран начинался словом «Начнём» и четырьмя группами выбора:
 * раскладка, операционная система, форма клавиатуры, с чего начать. Человек
 * отвечал на четыре вопроса о клавиатуре раньше, чем узнавал, зачем он здесь.
 *
 * Теперь сверху стоит то, ради чего сюда пришли: заголовок, честное
 * объяснение, одно действие и язык. Всё остальное — под раскрытием: у
 * раскладки, системы и формы есть разумные значения по умолчанию, и менять их
 * нужно меньшинству.
 *
 * Обещаний скорости здесь нет: продукт измеряет ровность набора и ошибки по
 * клавишам, а не то, когда человек «начнёт печатать вслепую».
 */
import { useState } from 'react';
import type { LanguageCode } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import { Button, ChoiceGroup } from '@type2x/ui';
import { defaultChoice, languageOptions, type OnboardingChoice } from '@type2x/feature-onboarding';

import { KeyboardChoiceFields } from './KeyboardChoiceFields.js';
import { OnboardingArt } from './OnboardingArt.js';

export interface OnboardingScreenProps {
  readonly t: Translate;
  readonly onStart: (choice: OnboardingChoice) => void;
}

export function OnboardingScreen({ t, onStart }: OnboardingScreenProps): React.JSX.Element {
  const [choice, setChoice] = useState<OnboardingChoice>(defaultChoice('ru'));

  function selectLanguage(language: LanguageCode): void {
    // Раскладка обязана соответствовать языку, иначе выбор будет несогласован.
    setChoice({ ...choice, language, layoutId: defaultChoice(language).layoutId });
  }

  return (
    <section className="t2x-onboarding">
      <div className="t2x-onboarding__lead">
        <h2 className="t2x-onboarding__title">{t('onboarding.title')}</h2>
        <p className="t2x-onboarding__explain">{t('onboarding.lead')}</p>

        <Button onClick={() => onStart(choice)}>
          {t(choice.start === 'fundamentals' ? 'onboarding.startBasics' : 'onboarding.start')}
        </Button>

        <ChoiceGroup
          legend={t('onboarding.language')}
          name="language"
          value={choice.language}
          options={languageOptions().map((option) => ({
            value: option.value,
            label: t(`onboarding.language.${option.value}`),
          }))}
          onChange={selectLanguage}
        />

        {/*
          Раскладка, система и форма клавиатуры — под раскрытием: значения по
          умолчанию верны для большинства, а четыре вопроса до первого нажатия
          верны не для кого.
        */}
        <details className="t2x-disclosure">
          <summary>{t('onboarding.setup')}</summary>
          <div className="t2x-disclosure__body">
            <KeyboardChoiceFields t={t} choice={choice} onChange={setChoice} />
          </div>
        </details>

        {/*
          Что измеряется — строкой, а подробности под раскрытием (ТЗ UI-37).
          Строка стоит на виду: обещание не собирать лишнего читают до
          согласия, а не после.
        */}
        <p className="t2x-muted t2x-onboarding__measures">{t('onboarding.measures')}</p>
        <details className="t2x-disclosure">
          <summary>{t('onboarding.measuresMore')}</summary>
          <div className="t2x-disclosure__body">
            <p className="t2x-muted">{t('onboarding.measuresBody')}</p>
            <p className="t2x-muted">{t('onboarding.guest')}</p>
          </div>
        </details>
      </div>

      <OnboardingArt />
    </section>
  );
}
